import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { MdHome, MdMenu, MdMap } from "react-icons/md";
import { FiLogOut } from "react-icons/fi";
import { BsClipboardDataFill } from "react-icons/bs";
import TrilhaNavegacao from "../trilha-navegacao";
import Logo from "../../assets/logo.png";
import styles from "./styles.module.css";
import { useAuth } from "../../contexts/AuthContext";

const BREAKPOINT_DESKTOP = 900;

const MENU_DEFINITIONS = [
  {
    to: "/",
    label: "Início",
    icon: <MdHome />,
    roles: ["administrador", "analista"],
  },
  {
    to: "/relatorios",
    label: "Relatórios",
    icon: <BsClipboardDataFill />,
    roles: ["administrador", "analista"],
    subItems: [
      { to: "/relatorios", label: "Resultados" },
      { to: "/relatorios/perfil-eleitorado", label: "Perfil do Eleitorado" },
    ],
  },
  {
    to: "/mapa",
    label: "Mapa Comparativo",
    icon: <MdMap />,
    roles: ["administrador", "analista"],
  },
];

const resolveFrontUrl = (envKey, developmentPort) => {
  const value = process.env[envKey];

  if (value) {
    return value;
  }

  if (process.env.NODE_ENV !== "production") {
    return `http://localhost:${developmentPort}`;
  }

  throw new Error(`Variavel de ambiente obrigatoria nao configurada: ${envKey}`);
};

const SYSTEM_LINKS = [
  {
    label: "CRM",
    url: resolveFrontUrl("REACT_APP_CRM_FRONT_URL", 5174),
  },
  {
    label: "Acesso MindPolis",
    url: resolveFrontUrl("REACT_APP_ACESSO_FRONT_URL", 5173),
  },
];

export default function Layout({ children }) {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [loadingLogout, setLoadingLogout] = useState(false);

  const { user, roles } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = () => window.innerWidth <= BREAKPOINT_DESKTOP;

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile()) setDrawerOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!drawerOpen && !sidebarExpanded) setOpenSubmenu(null);
  }, [drawerOpen, sidebarExpanded]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => setSidebarExpanded((v) => !v);

  const toggleSubmenu = (idx) =>
    setOpenSubmenu((prev) => (prev === idx ? null : idx));

  const handleMenuToggle = () => {
    isMobile() ? setDrawerOpen((prev) => !prev) : toggleSidebar();
  };

  const { logout } = useAuth();
  const handleLogout = async () => {
    if (loadingLogout) return;
    setLoadingLogout(true);
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Erro no logout:", err);
    } finally {
      setLoadingLogout(false);
    }
  };

  const filteredMenu = MENU_DEFINITIONS.filter(
    (item) => user && item.roles.some((role) => roles.includes(role))
  );

  useEffect(() => {
    const indiceSubmenuAtivo = filteredMenu.findIndex(
      (item) =>
        item.subItems &&
        item.subItems.some((sub) => location.pathname.startsWith(sub.to))
    );

    if (indiceSubmenuAtivo >= 0) {
      setOpenSubmenu(indiceSubmenuAtivo);
    }
  }, [location.pathname, filteredMenu]);

  const renderMenuItem = useCallback(
    (item, idx) => {
      const hasChildren = !!item.subItems;
      const isSubOpen = openSubmenu === idx;
      const subActive =
        hasChildren &&
        item.subItems.some((sub) => location.pathname.startsWith(sub.to));

      return (
        <li key={idx} className={styles.menuItemWrapper}>
          <div className={styles.menuRow}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `${styles.menuLink} ${
                  isActive && !hasChildren
                    ? styles.active
                    : subActive && !isSubOpen
                      ? styles.active
                      : ""
                }`
              }
              onClick={(e) => {
                if (hasChildren) {
                  e.preventDefault();
                  toggleSubmenu(idx);
                }
              }}
            >
              <span className={styles.icon}>{item.icon}</span>
              {sidebarExpanded && (
                <>
                  <span className={styles.label}>{item.label}</span>
                  {hasChildren && (
                    <span
                      className={`${styles.chevron} ${
                        isSubOpen ? styles.chevronOpen : ""
                      }`}
                    >
                      ▾
                    </span>
                  )}
                </>
              )}
            </NavLink>
          </div>

          {hasChildren && isSubOpen && (
            <ul className={styles.submenu}>
              {item.subItems.map((sub, sidx) => (
                <li key={sidx}>
                  <NavLink
                    to={sub.to}
                    className={({ isActive }) =>
                      `${styles.submenuLink} ${isActive ? styles.activeSub : ""}`
                    }
                  >
                    {sub.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    },
    [openSubmenu, location.pathname, sidebarExpanded]
  );

  return (
    <div className={styles.wrapper}>
      <header className={styles.topBar}>
        <div className={styles.leftSection}>
          <button
            aria-label="Abrir menu"
            className={styles.mobileHamburger}
            onClick={handleMenuToggle}
          >
            <MdMenu />
          </button>
          <div className={styles.brand}>
            <img className={styles.logo} src={Logo} alt="Voto Visão" />
          </div>
        </div>

        <div className={styles.rightSection}>
          <button
            onClick={handleLogout}
            className={styles.logoutButton}
            aria-label="Sair"
            disabled={loadingLogout}
          >
            <span className={styles.iconWrapper}>
              <FiLogOut />
            </span>
            <span className={styles.logoutText}>Sair</span>
          </button>
        </div>
      </header>

      <aside
        className={`
          ${styles.sidebar}
          ${sidebarExpanded ? styles.expanded : styles.collapsed}
          ${drawerOpen ? styles.drawerOpen : ""}
        `}
        aria-label="Menu principal"
      >
        <nav className={styles.sidebarNav}>
          <ul className={styles.menuList}>{filteredMenu.map(renderMenuItem)}</ul>
        </nav>

        {sidebarExpanded && (
          <div className={styles.systemSwitcher}>
            <span>Outros sistemas</span>
            {SYSTEM_LINKS.map((system) => (
              <a key={system.label} href={system.url}>
                {system.label}
              </a>
            ))}
          </div>
        )}
      </aside>

      <main className={styles.content}>
        <TrilhaNavegacao />
        <div className={styles.childrenContainer}>{children}</div>
      </main>
    </div>
  );
}
